<script lang="ts">
import type { StudyDistributionSlideProps } from '$lib/components/study/slide-layout-types'
import StudyBlueCalloutCard from '../cards/StudyBlueCalloutCard.svelte'
import StudyChargeIqrStripCard from '../cards/StudyChargeIqrStripCard.svelte'
import StudyChargeQuartileStatGridCard from '../cards/StudyChargeQuartileStatGridCard.svelte'
import StudySlide from '../StudySlide.svelte'
import StudySlideContent from '../StudySlideContent.svelte'

function computeChargeIqrViz(d: { p25: number; median: number; p75: number; mean: number }) {
  const p25 = d.p25
  const med = d.median
  const p75 = d.p75
  const mean = d.mean
  const rangeMax = Math.max(p75 * 1.08, mean * 1.05, 1)
  return {
    p25,
    med,
    p75,
    mean,
    rangeMax,
    p25Pct: (p25 / rangeMax) * 100,
    p75Pct: (p75 / rangeMax) * 100,
    iqrW: Math.max(((p75 - p25) / rangeMax) * 100, 0.8),
    medPct: (med / rangeMax) * 100,
    meanPct: (mean / rangeMax) * 100,
  }
}

/**
 * Per-claim charge distribution (`s6c`): `.ey` / `.hl` / `.bd` + `StudyChargeIqrStripCard` +
 * `StudyChargeQuartileStatGridCard` + `StudyBlueCalloutCard`.
 */
let props: StudyDistributionSlideProps = $props()

const headlineStyle = props.headlineStyle ?? 'margin-bottom:.75rem;'
const introStyle = props.introStyle ?? 'margin-bottom:1rem;max-width:720px;'

const viz = $derived.by(() => computeChargeIqrViz(props.distribution))

const stripSubtitleHtml = $derived(
  props.stripSubtitleHtml ??
    `${props.stripSubtitlePrefixHtml ?? 'IQR, median, and mean on a $0&ndash;'}${props.formatSpend(viz.rangeMax)}${props.stripSubtitleSuffixHtml ?? ' scale &middot; all-time &middot; IMX Data'}`,
)
</script>

<StudySlide
  id={props.id}
  active={props.active}
  extraBottom={props.extraBottom}
  background={props.background}
  slideClass={props.slideClass}
>
  <StudySlideContent
    maxWidth={props.maxWidth ?? '980px'}
    contentClass={props.contentClass}
    style={props.contentStyle}
  >
    <div class="ey">{@html props.eyebrow}</div>
    <div class="hl" style={headlineStyle}>{@html props.headline}</div>
    <p class="bd" style={introStyle}>{@html props.introHtml}</p>

    <StudyChargeIqrStripCard
      title={props.stripTitle}
      titleStyle={props.stripTitleStyle}
      subtitleHtml={stripSubtitleHtml}
      subtitleStyle={props.stripSubtitleStyle}
      {viz}
      formatSpend={props.formatSpend}
      legendHtml={props.stripLegendHtml}
    />

    <StudyChargeQuartileStatGridCard
      stats={props.distribution}
      formatSpend={props.formatSpend}
      gridStyle={props.statGridStyle}
    />

    <StudyBlueCalloutCard
      bodyHtml={props.insightHtml}
      cardStyle={props.insightCardStyle ?? 'margin-top:.85rem;'}
    />
  </StudySlideContent>
</StudySlide>
